import { useCallback, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import {
  useDataStateSelector,
  useDataSettings,
  useDataSetStatus
} from "../../hooks/useData";
import usePermissions from "../../hooks/usePermissions";
import useSession from "../../hooks/useSession";
import useConfirmNavigation from "../../hooks/useConfirmNavigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faUserAstronaut,
  faSpinner,
  faCog,
  faFingerprint,
  faUser,
  faUsers,
  faWifi,
  faFlask,
  faLock
} from "@fortawesome/free-solid-svg-icons";

import {
  Navbar,
  Nav,
  NavItem,
  NavDropdown,
  OverlayTrigger,
  Popover,
  Button,
  ButtonGroup,
  DropdownButton,
  Dropdown
} from "react-bootstrap";
import { userSelectBranch } from "../../redux/actions/user";
//import { userLogOut, checkUserLogged } from "../../redux/actions/user";

import Nick from "./nick";
import DataPanel from "./dataPanel";
import UsersPanel from "./usersPane";

import "./style.scss";
import { faCodeCompare } from "@fortawesome/free-solid-svg-icons";
const url = require("url");
const navStyle = { color: "greenyellow" };

export default function NavigationBar(props) {
  const dispatch = useDispatch();
  const submitBugRef = useRef({});
  const listBugRef = useRef({});
  const setDataStatus = useDataSetStatus();
  const confirmNavigation = useConfirmNavigation();
  const { saving, loading } = useDataStateSelector((state) => ({
    saving: state.saving,
    loading: state.loading
  }));
  const location = useLocation();
  const { logout, isLogged } = useSession();
  const menuItems = useDataSettings("menuItems");
  const {
    name,
    nickname,
    tenant,
    branch,
    branches,
    picture,
    loggedStatus,
    loggedUsers: logged
  } = useSelector((state) => state.user);
  const permissions = usePermissions();

  useEffect(() => {
    setTimeout(() => isLogged(tenant), 500);
    const checker = setInterval(() => isLogged(tenant), 15000);

    return () => clearInterval(checker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isActive = useCallback(
    (href) => {
      if (href === "/") return location.pathname === "/";
      return location.pathname.includes(href);
    },
    [location.pathname]
  );

  const navClick = useCallback(
    (e) => {
      e.preventDefault();
      const destination = url.parse(e.target.href).pathname;

      confirmNavigation(destination);
    },
    [confirmNavigation]
  );

  const doLogout = useCallback(
    () =>
      confirmNavigation("", (proceed) => proceed && logout(), {
        leaveAll: true
      }),
    [confirmNavigation, logout]
  );

  const menuBar = useMemo(() => {
    return (
      <Nav className="mr-auto" defaultActiveKey="/">
        {(menuItems || []).map((item, i) => {
          if (item.hidden) return null;

          if (item.divider)
            return (
              <NavDropdown.Divider key={`menu-nav-item-${item.label}-${i}`} />
            );

          if (!item.permission.includes("*")) {
            const itemPerm = Array.isArray(item.permission)
              ? item.permission
              : [item.permission];

            if (!permissions.read.some((k) => itemPerm.includes(k)))
              return null;
          }

          if (item.sub) {
            return (
              <NavDropdown
                key={`nav-drop-${item.label}-${i}`}
                title={
                  <>
                    {item.analiticaIcon ? (
                      <FontAwesomeIcon
                        icon={faFlask}
                        style={{
                          marginRight: "4px",
                          color: "#ff1493",
                          fontSize: "0.8em"
                        }}
                      />
                    ) : null}
                    {item.analiticaIcon ? (
                      <span style={{ color: "#ff1493", fontWeight: 500 }}>
                        {item.label}
                      </span>
                    ) : (
                      item.label
                    )}
                  </>
                }
                id={`nav-drop-${item.label}-${i}`}
                className="menu-label-item"
              >
                {item.sub.map((d, k) => {
                  if (d.hidden) return null;

                  if (d.divider)
                    return (
                      <NavDropdown.Divider
                        key={`menu-item-drop-${item.label}-${k}`}
                      />
                    );

                  if (d.header)
                    return (
                      <NavDropdown.Header
                        key={`menu-item-drop-${d.header}-${k}`}
                      >
                        {d.header}
                      </NavDropdown.Header>
                    );

                  if (!d.permission.includes("*")) {
                    const itemPerm = Array.isArray(d.permission)
                      ? d.permission
                      : [d.permission];
                    if (!permissions.read.some((k) => itemPerm.includes(k)))
                      return null;
                  }

                  return (
                    <NavDropdown.Item
                      key={`menu-item-drop-${d.label}-${k}`}
                      href={d.href}
                      onClick={d.disabled ? undefined : navClick}
                      disabled={d.disabled}
                    >
                      {d.label}
                    </NavDropdown.Item>
                  );
                })}
              </NavDropdown>
            );
          }
          return (
            <NavItem
              key={`menu-nav-item-${item.label}-${i}`}
              eventkey={i}
              className="menu-label-item"
            >
              <Nav.Link
                key={`menu-item-${item.label}-${i}`}
                href={item.href}
                onClick={navClick}
                active={isActive(item.href)}
              >
                {item.label}
              </Nav.Link>
            </NavItem>
          );
        })}
      </Nav>
    );
  }, [isActive, menuItems, navClick, permissions]);

  const userMenu = useMemo(() => {
    return (
      <Nav className="user-menu">
        <div className="tenant-branch">
          <ButtonGroup>
            <Button
              size="sm"
              id="tenant-button"
              variant={
                tenant.includes("prod") ? "outline-warning" : "outline-info"
              }
            >
              <div className="tenant-branch-container">
                {loading ? (
                  <div className="loading-branch">
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </div>
                ) : null}
                {tenant}
              </div>
            </Button>
            <DropdownButton
              as={ButtonGroup}
              title={
                branch === "main" ? (
                  <div className="tenant-branch-container">
                    {branch}{" "}
                    <FontAwesomeIcon icon={faLock} className="lock-icon" />
                  </div>
                ) : (
                  branch
                )
              }
              id="branch-dropdown"
              size="sm"
              variant="outline-light"
            >
              {branches[tenant].map((b) => (
                <Dropdown.Item
                  key={b}
                  eventKey={b}
                  onClick={() => {
                    confirmNavigation(
                      "",
                      (proceed) => {
                        if (proceed) {
                          setDataStatus("unsavedData", false);
                          dispatch(userSelectBranch(b));
                        }
                      },
                      // The drafts are of the branch loaded now
                      { leaveAll: true }
                    );
                  }}
                  className="branch-item"
                >
                  {b}
                </Dropdown.Item>
              ))}
            </DropdownButton>
            <Button
              size="sm"
              variant="outline-light"
              disabled={!permissions.read.includes("branches")}
              onClick={() => confirmNavigation(`/branches`)}
            >
              <FontAwesomeIcon icon={faCodeCompare} size="sm" />
            </Button>
          </ButtonGroup>
        </div>
        <NavDropdown
          style={{ marginRight: "30px" }}
          title={
            <span>
              <Nick name={name} tenant={tenant} picture={picture} />
            </span>
          }
          id="user-drop-down"
          //alignright
        >
          <NavDropdown.Item
            onClick={() => confirmNavigation(`/users/${nickname}`)}
          >
            <FontAwesomeIcon icon={faUserAstronaut} className="menuIcon" />
            Profile
          </NavDropdown.Item>

          <NavDropdown.Divider />
          <NavDropdown.Item onClick={doLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="menuIcon" />
            Logout
          </NavDropdown.Item>
        </NavDropdown>
      </Nav>
    );
  }, [
    doLogout,
    nickname,
    picture,
    tenant,
    dispatch,
    branches,
    branch,
    setDataStatus,
    confirmNavigation,
    permissions?.expert,
    submitBugRef,
    listBugRef
  ]);

  const loggedUsersPane = useMemo(() => {
    if (!logged?.length > 0) return null;

    return (
      <OverlayTrigger
        key="data-panel"
        placement="bottom"
        trigger="click"
        overlay={
          <Popover id={`tooltip-logged-panel`} bsPrefix="ares-logged-panel">
            <UsersPanel />
          </Popover>
        }
      >
        <div className="loggedUsers">
          <FontAwesomeIcon
            icon={logged.length > 1 ? faUsers : faUser}
            className="userIcon"
          />
          {logged.length}
        </div>
      </OverlayTrigger>
    );
  }, [logged]);

  const loggedPane = useMemo(() => {
    return (
      <div className="signal-pane">
        <FontAwesomeIcon
          icon={faWifi}
          className={
            loggedStatus?.status ? "OK" : loggedStatus?.reason ? "KK" : "KO"
          }
          onDoubleClick={() => confirmNavigation(`/eastereggs/sudares`)}
        />
        {!loggedStatus?.status ? (
          <Button size="sm" variant="outline-danger" onClick={() => logout()}>
            {loggedStatus?.reason || "Not Logged"}
            {loggedStatus?.API ? (
              <div className="API-VERSION">{loggedStatus?.API}</div>
            ) : null}
          </Button>
        ) : (
          <div className="API-VERSION">{loggedStatus?.API}</div>
        )}
      </div>
    );
  }, [
    loggedStatus?.API,
    loggedStatus?.reason,
    loggedStatus?.status,
    logout,
    confirmNavigation
  ]);

  return (
    <>
      {/* <SubmitBug ref={submitBugRef} />
      <ListBug ref={listBugRef} /> */}
      <div className="row">
        <Navbar expand="xl" fixed="top" variant="dark">
          <Navbar.Brand href="#home" style={navStyle}>
            <OverlayTrigger
              key="data-panel"
              placement="bottom"
              trigger="click"
              overlay={
                permissions.read.includes("admin") ? (
                  <Popover id={`tooltip-data-panel`} bsPrefix="ares-data-panel">
                    <DataPanel />
                  </Popover>
                ) : (
                  <div></div>
                )
              }
            >
              <div>
                <FontAwesomeIcon
                  icon={loading ? faCog : saving ? faSpinner : faFingerprint}
                  spin={saving || loading}
                />{" "}
                <span className="logo-main">4R3S</span>
              </div>
            </OverlayTrigger>
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="responsive-navbar-nav"
            className="nav-toggle-button"
          />

          <Navbar.Collapse id="responsive-navbar-nav">
            {menuBar}
            <div className="nav-divider"></div>
            <div className="toolbar">
              {loggedUsersPane}
              {loggedPane}
            </div>

            {userMenu}
          </Navbar.Collapse>
        </Navbar>
      </div>
    </>
  );
}
